/**
 * Modelos de nota marcados como "padrão" (estrela) — por tipo de nota
 * (`kindDiscriminator` do `NotesWithTemplates`: 'prescriptionNotesV1',
 * 'examNotesV1'…), persistidos por navegador como os padrões da receita de
 * óculos (`lensObservations.ts`).
 *
 * Guardamos id + texto: o id é pra estrela acender na lista; o texto é pra
 * compor sem rede (o ditado, spec 009, monta a receita fora do React).
 * Pedido do Leopoldo, 17/09/2026: receita de medicamentos ditada já sai com
 * as orientações padrão.
 */

import { looksLikeHtml } from '@/utils/richText';
import { phrasesMatch, triggerKey, type TriggerMap } from '@/utils/phraseTriggers';

export interface NotesDefault {
  id: string;
  title: string;
  notes: string;
}

const KEY_PREFIX = 'dratento-notes-defaults:';

function ls(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

export function loadNotesDefaults(kind: string): NotesDefault[] {
  try {
    const raw = ls()?.getItem(KEY_PREFIX + kind);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is NotesDefault =>
        !!x && typeof x === 'object' && typeof (x as NotesDefault).id === 'string' && typeof (x as NotesDefault).notes === 'string',
    );
  } catch {
    return [];
  }
}

export function saveNotesDefaults(kind: string, list: NotesDefault[]) {
  ls()?.setItem(KEY_PREFIX + kind, JSON.stringify(list));
}

export function toggleNotesDefault(kind: string, item: NotesDefault): NotesDefault[] {
  const cur = loadNotesDefaults(kind);
  const next = cur.some((d) => d.id === item.id) ? cur.filter((d) => d.id !== item.id) : [...cur, item];
  saveNotesDefaults(kind, next);
  return next;
}

/** Junta notas respeitando HTML (RichTextEditor) ou texto puro. */
export function joinNotes(parts: string[]): string {
  const limpos = parts.map((p) => p.trim()).filter(Boolean);
  if (limpos.length === 0) return '';
  return limpos.some(looksLikeHtml) ? limpos.join('<br>') : limpos.join('\n');
}

function norm(s: string): string {
  return s.replace(/<[^>]+>/g, ' ').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
}

/**
 * Notas da receita ditada = padrões (na ordem em que foram marcados) + o que
 * o médico ditou, sem repetir o que já está nos padrões.
 */
export function composeNotesWithDictated(dictated: string, defaults: string[]): string {
  // Base sem repetição (um modelo pode ser padrão E casar com a fala).
  const base: string[] = [];
  for (const d of defaults) {
    const t = d.trim();
    if (t && !base.some((b) => norm(b) === norm(t))) base.push(t);
  }
  const d = dictated.trim();
  const jaTem = d && base.some((b) => norm(b) === norm(d) || norm(b).includes(norm(d)));
  return joinNotes(jaTem || !d ? base : [...base, d]);
}

/** Palavras do título que não dizem nada sozinhas. */
const TITULO_IGNORAR = new Set(['de', 'do', 'da', 'e', 'o', 'a', 'os', 'as', 'em', 'no', 'na', 'com', 'para', 'ate', 'sem']);

function palavrasTitulo(titulo: string): string[] {
  return norm(titulo)
    .replace(/[^a-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length >= 3 && !TITULO_IGNORAR.has(w));
}

/**
 * Prefixos que só existem presos a outra palavra ("pré-operatório",
 * "pós-operatório", "anti-inflamatório"). A normalização troca o hífen por
 * espaço, e o "pre" solto passava por começo de "prescrevo", "pressão" e
 * "precisa"; o "pos", de "posterior" e "posologia". Esses só valem inteiros.
 */
export const PREFIXOS_PRESOS = new Set([
  'pre', 'pos', 'pro', 'sub', 'anti', 'contra', 'semi', 'extra', 'intra', 'inter', 'super', 'ultra',
  'infra', 'micro', 'macro', 'multi', 'auto', 'retro', 'trans', 'peri',
]);

/**
 * Terminações de flexão (número, gênero, verbo), da mais longa pra mais
 * curta: "operado"/"operada"/"operou", "suspender"/"suspenda",
 * "exame"/"exames", "orientação"/"orientações".
 */
const TERMINACOES = [
  'aram', 'eram', 'iram', 'ando', 'endo', 'indo', 'ados', 'adas', 'idos', 'idas',
  'ado', 'ada', 'ido', 'ida', 'oes', 'aes', 'ais', 'eis', 'ois',
  'ar', 'er', 'ir', 'am', 'em', 'ou', 'eu', 'iu', 'ei', 'as', 'os', 'es', 'is', 'ao',
  'a', 'o', 'e', 's',
];

/** A palavra sem a terminação de flexão (sobram ao menos 4 letras). */
function radical(w: string): string {
  const fim = TERMINACOES.find((t) => w.endsWith(t) && w.length - t.length >= 4);
  return fim ? w.slice(0, w.length - fim.length) : w;
}

/**
 * A palavra `w` do título foi dita na palavra `t` da fala?
 *  - igual;
 *  - a mesma com outra flexão ("operada" ↔ OPERADO, "suspenda" ↔ SUSPENDER);
 *  - título abreviado ("CIR CAT AGEND"): abreviação corta antes da vogal,
 *    então palavra do título terminada em consoante vale como começo da dita
 *    ("cirurgia", "catarata", "agendar").
 *
 * Palavra CURTA da fala nunca vale como começo de palavra do título. Até
 * 08/10/2026 valia, e o artigo "o" casava com OLHO e com OPERADO, "a"/"av"
 * com AVALIAÇÃO, "e" com ENVIAR e EXAMES — a nota entrava sem ser dita.
 */
function palavraDita(w: string, t: string): boolean {
  if (t === w) return true;
  if (t.length >= 4 && w.length >= 4 && radical(t) === radical(w)) return true;
  return w.length >= 3 && /[^aeiouys]$/.test(w) && !PREFIXOS_PRESOS.has(w)
    && t.length > w.length && t.startsWith(w);
}

/**
 * Título de várias palavras foi dito: todas elas PERTO uma da outra (em
 * qualquer ordem), numa janela de poucas palavras — cabe "não suspender o
 * uso do colírio até o retorno". Espalhadas pela fala ("olho direito" numa
 * frase, "foi operado do joelho" três frases depois) não é o título.
 */
function tituloNaFala(ws: string[], tokens: string[]): boolean {
  const janela = ws.length + 6;
  const onde = ws.map((w) => tokens.flatMap((t, i) => (palavraDita(w, t) ? [i] : [])));
  if (onde.some((p) => p.length === 0)) return false;
  return onde.flat().some((ini) => onde.every((p) => p.some((i) => i >= ini && i < ini + janela)));
}

/**
 * Modelo que é FORMULÁRIO por preencher, não nota: campos entre colchetes
 * ("[AXIAL_OD]", "[K1_OD]", "[NOME_PACIENTE]"), que nada no caminho do
 * ditado preenche. O modelo "Avaliação de catarata" (semente da clínica)
 * entrava inteiro nas observações da cirurgia, uma linha de biometria por
 * etiqueta, e sairia assim no papel (visto em campo, 08/10/2026).
 */
export function isFillInForm(texto: string): boolean {
  const campos = (texto ?? '').match(/\[[A-ZÀ-Ý][A-ZÀ-Ý0-9_]+\]/g) ?? [];
  return campos.length >= 2 || campos.some((c) => c.includes('_'));
}

/**
 * Modelos de nota cujo TÍTULO foi dito ("NÃO SUSPENDER" ↔ "não suspender o
 * colírio sem recomendação"): todas as palavras significativas do título,
 * ditas juntas (`tituloNaFala`), cada uma inteira ou com outra flexão
 * (`palavraDita`). "NÃO SUSPENDER ATÉ RETORNO" só casa se "retorno" também
 * foi dito. Formulário por preencher (`isFillInForm`) nunca entra sozinho —
 * continua no "+ nota…" do card.
 */
export function matchNoteTemplates<T extends { title: string; notes: string }>(
  fala: string,
  templates: T[],
  /**
   * Gatilhos de frase por título ("uso contínuo" → NÃO SUSPENDER). Casa
   * ANTES do título: é a forma como o médico realmente fala.
   */
  triggers: TriggerMap = {},
): T[] {
  const texto = norm(fala);
  if (!texto) return [];
  const tokens = texto.replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/).filter(Boolean);
  return templates.filter((t) => {
    if (isFillInForm(t.notes)) return false;
    const gatilhos = triggers[triggerKey(t.title)];
    if (gatilhos && gatilhos.length > 0 && phrasesMatch(fala, gatilhos)) return true;
    const ws = palavrasTitulo(t.title);
    // Título de uma palavra só ("GELADEIRA") exige a palavra inteira.
    if (ws.length === 0) return false;
    if (ws.length === 1) return tokens.includes(ws[0]);
    return tituloNaFala(ws, tokens);
  });
}

/** Palavras do pedido em volta do título ditado ("anota nas orientações: intervalo"). */
const PEDIDO_DE_NOTA = new Set([
  'anota', 'anote', 'anotar', 'nota', 'notas', 'orientacao', 'orientacoes', 'coloca', 'coloque', 'colocar',
  'poe', 'bota', 'botar', 'escreve', 'escrever', 'receita', 'tambem', 'mais', 'nas', 'nos', 'pra',
]);

/**
 * Orientação ditada que é só o TÍTULO de modelos da clínica ("não suspender;
 * intervalo"): o texto dos modelos já entra pelo título (`matchNoteTemplates`),
 * então o título não sai no papel como linha solta ao lado dele. Trecho com
 * algo além do título ou do gatilho ("intervalo de 10 minutos") fica como foi
 * dito — o médico disse mais do que o modelo diz. Pedido de 25/10/2026.
 */
export function withoutTemplateTitles<T extends { title: string; notes: string }>(
  ditado: string,
  templates: T[],
  triggers: TriggerMap = {},
): string {
  const trechos = (ditado ?? '').split(/[;\n]+/).map((s) => s.trim()).filter(Boolean);
  const ficam = trechos.filter((trecho) => {
    const casados = matchNoteTemplates(trecho, templates, triggers);
    if (casados.length === 0) return true;
    const doModelo = casados.flatMap((t) => [
      ...palavrasTitulo(t.title),
      ...(triggers[triggerKey(t.title)] ?? []).flatMap(palavrasTitulo),
    ]);
    const tokens = norm(trecho).replace(/[^a-z0-9 ]+/g, ' ').split(/\s+/).filter(Boolean);
    return tokens.some((t) => !TITULO_IGNORAR.has(t) && !PEDIDO_DE_NOTA.has(t) && !doModelo.some((w) => palavraDita(w, t)));
  });
  return ficam.length === trechos.length ? ditado : ficam.join('; ');
}

/**
 * Notas → itens, pra virarem etiquetas removíveis no card. Aceita os dois
 * separadores que `joinNotes` produz (texto puro e HTML).
 */
export function splitNotes(notes: string): string[] {
  return (notes ?? '')
    .split(/<br\s*\/?>|\n/i)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Tira UM item da linha de notas (o X da etiqueta). */
export function dropNoteLine(notes: string, index: number): string {
  const itens = splitNotes(notes);
  if (index < 0 || index >= itens.length) return notes;
  return joinNotes(itens.filter((_, i) => i !== index));
}

/**
 * União de duas notas (pra fundir ditados), LINHA A LINHA: cada linha da
 * segunda entra só se ainda não estiver na primeira.
 *
 * Era por bloco inteiro: o segundo ditado trazia "linha já existente +
 * linha nova", o bloco não "estava contido" e entrava todo — a linha antiga
 * saía duas vezes no papel (visto em campo, 27/09/2026: "Caso queira enviar
 * o resultado…" repetido nas orientações do pedido).
 */
export function mergeNotes(a: string, b: string): string {
  const bb = b.trim();
  if (!bb) return a;
  if (!a.trim()) return bb;
  const atual = norm(a);
  const vistas = new Set(splitNotes(a).map(norm));
  const faltam: string[] = [];
  for (const linha of splitNotes(bb)) {
    const n = norm(linha);
    if (!n || vistas.has(n) || atual.includes(n)) continue;
    vistas.add(n);
    faltam.push(linha);
  }
  return faltam.length === 0 ? a : joinNotes([a, ...faltam]);
}

// ── O que o médico tirou de um card não volta (08/10/2026) ──
//
// Cada ditado traz de novo as notas padrão e os modelos que casaram, e a
// fusão (`mergeNotes`) devolvia ao card a linha que o médico tinha acabado
// de tirar no ×. As linhas tiradas ficam no card, normalizadas.

/** Lembra as linhas tiradas (o × da etiqueta, ou apagadas no "Editar"). */
export function rememberRemovedLines(tiradas: readonly string[] | undefined, linhas: string[]): string[] | undefined {
  const out = new Set(tiradas ?? []);
  for (const l of linhas) {
    const n = norm(l);
    if (n) out.add(n);
  }
  return out.size > 0 ? [...out] : undefined;
}

/** Esquece as linhas que o médico pôs de volta ("+ nota…" ou digitadas). */
export function forgetRemovedLines(tiradas: readonly string[] | undefined, notes: string): string[] | undefined {
  if (!tiradas?.length) return undefined;
  const voltaram = new Set(splitNotes(notes).map(norm));
  const resto = tiradas.filter((n) => !voltaram.has(n));
  return resto.length > 0 ? resto : undefined;
}

/** Notas editadas à mão: a linha que saiu fica lembrada; a que está lá, não. */
export function trackEditedLines(tiradas: readonly string[] | undefined, antes: string, depois: string): string[] | undefined {
  const ficaram = new Set(splitNotes(depois).map(norm));
  const sairam = splitNotes(antes).filter((l) => !ficaram.has(norm(l)));
  return forgetRemovedLines(rememberRemovedLines(tiradas, sairam), depois);
}

/** Notas de um ditado novo sem as linhas que o médico já tirou do card. */
export function withoutRemovedLines(notes: string, tiradas: readonly string[] | undefined): string {
  if (!tiradas?.length || !notes.trim()) return notes;
  const fora = new Set(tiradas);
  const linhas = splitNotes(notes);
  const ficam = linhas.filter((l) => !fora.has(norm(l)));
  return ficam.length === linhas.length ? notes : joinNotes(ficam);
}
